<script setup lang="ts">
import { computed } from "vue";

export interface SpinnerProps {
  /** A string representing the ARIA label for the element. */
  ariaLabel?: string;
  /** A string representing the size of the element. */
  size?: "small" | "medium" | "large";
}

const { ariaLabel, size = "medium" } = defineProps<SpinnerProps>();

const spinnerClassName = {
  small: { gap: "gap-px", size: "h-2 w-2" },
  medium: { gap: "gap-0.5", size: "h-4 w-4" },
  large: { gap: "gap-0.5", size: "h-6 w-6" },
};

const ariaAttributes = computed(() => ({
  "aria-busy": "true",
  "aria-live": "polite",
  "aria-label": ariaLabel || "Loading…",
  role: "status",
}));
</script>

<template>
  <div>
    <div
      class="clr-spinner grid w-fit grid-cols-3"
      :class="spinnerClassName[size].gap"
      v-bind="ariaAttributes"
      data-testid="spinner"
    >
      <div
        v-for="(color, index) in [
          'bg-dark-blue',
          'bg-dark-blue',
          'bg-dark-blue',
          'bg-medium-blue',
          'bg-medium-blue',
          'bg-medium-blue',
          'bg-light-blue',
          'bg-light-blue',
          'bg-light-blue',
        ]"
        :key="index"
        class="rounded-full"
        :class="`${color} ${spinnerClassName[size].size}`"
      ></div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.clr-spinner {
  div {
    animation: 3000ms cubic-bezier(0.445, 0.05, 0.55, 0.95) infinite;

    &:nth-child(1),
    &:nth-child(5),
    &:nth-child(9) {
      animation-name: bullet-odd;
      animation-delay: 0.6s;
    }

    &:nth-child(2),
    &:nth-child(6) {
      animation-name: bullet-even;
      animation-delay: 0.6s;
    }

    &:nth-child(3) {
      animation-name: bullet-odd;
      animation-delay: 1s;
    }

    &:nth-child(4),
    &:nth-child(8) {
      animation-name: bullet-even;
      animation-delay: 0s;
    }

    &:nth-child(7) {
      animation-name: bullet-odd;
    }
  }
}

@keyframes bullet-odd {
  0%,
  20%,
  86.67%,
  100% {
    transform: scale(0.1, 0.1);
    opacity: 0;
  }

  36.67%,
  70% {
    transform: scale(1, 1);
    opacity: 1;
  }
}

@keyframes bullet-even {
  0%,
  30%,
  93.33%,
  100% {
    transform: scale(0.1, 0.1);
    opacity: 0;
  }

  46.67%,
  76.67% {
    transform: scale(1, 1);
    opacity: 1;
  }
}
</style>
